/**
 * FontSettingsModal.tsx
 *
 * Standard React font settings modal using HTML/CSS
 * Based on Ego Somnium font settings modal
 */
import React from 'react';
import { useSettings } from '../../services/SettingsContext';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';

interface FontSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Web-safe fonts including accessibility options
const WEB_FONT_OPTIONS: { value: string; label: string }[] = [
  // System defaults
  { value: 'system-ui', label: 'System UI (Recommended)' },
  { value: 'sans-serif', label: 'Sans Serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'monospace', label: 'Monospace' },
  // Common web fonts
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Palatino', label: 'Palatino' },
  { value: 'Garamond', label: 'Garamond' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { value: 'Arial Black', label: 'Arial Black' },
  { value: 'Impact', label: 'Impact' },
];

const FontSettingsModal: React.FC<FontSettingsModalProps> = ({ isOpen, onClose }) => {
  const { fontSettings, setFontSettings } = useSettings();
  const { currentTheme: colors } = useTheme();

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Font Settings">
      {/* Font Size Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
          Font Size (10-24pt)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: colors.textMuted, fontSize: 14 }}>10</span>
          <input
            data-testid="font-size-slider"
            type="range"
            min={10}
            max={24}
            step={1}
            value={fontSettings.fontSize}
            onChange={e => setFontSettings({ fontSize: parseInt(e.target.value) })}
            style={{ flex: 1, accentColor: colors.primary }}
          />
          <span style={{ color: colors.textMuted, fontSize: 14 }}>24</span>
          <span style={{ color: colors.primary, fontWeight: 'bold', fontSize: 14, minWidth: 45, textAlign: 'right' }}>
            {fontSettings.fontSize}pt
          </span>
        </div>
      </div>

      {/* Preview of current font size */}
      <div style={{
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
        borderStyle: 'solid',
        borderRadius: 8,
        backgroundColor: `${colors.primary}11`,
        marginBottom: 24,
      }}>
        <div style={{ fontSize: fontSettings.fontSize, color: colors.textMain }}>
          The quick brown fox jumps over the lazy dog
        </div>
      </div>

      {/* Font Family Section */}
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
        Font Family ({WEB_FONT_OPTIONS.length} web-safe fonts)
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {WEB_FONT_OPTIONS.map(option => (
          <button
            key={option.value}
            data-testid={`font-family-option-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => setFontSettings({ fontFamily: option.value })}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: fontSettings.fontFamily === option.value ? `${colors.primary}15` : 'transparent',
              border: 'none',
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
              borderBottomStyle: 'solid',
              cursor: 'pointer',
            }}
          >
            <span style={{
              color: colors.textMain,
              fontSize: 14,
              fontFamily: option.value,
              flex: 1,
              textAlign: 'left',
            }}>
              {option.label}
            </span>
            {fontSettings.fontFamily === option.value && (
              <span style={{ color: colors.primary, fontSize: 18 }}>✓</span>
            )}
          </button>
        ))}
      </div>
    </ScrollableModal>
  );
};

export { FontSettingsModal };
